// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// A content-addressed cache of machine definitions (G1-03, 08 §8).
//
// **Keyed by the definition's hash, not by its id.** A published version is immutable — 04 §1 makes
// that one of the format's four principles and the whole leaderboard rests on it — so the same hash
// is the same bytes for ever, and there is no invalidation problem to have. A cache keyed by
// `machineId` would have one, and would need a rule about when to distrust itself; this one simply
// misses when the hash changes, which is the correct answer with no rule attached.
//
// It is the same reasoning the roster is shaped around. `GET /machines` is small and carries each
// machine's current `definitionHash`; `GET /machines/{id}/versions/{semver}` is tens of kilobytes
// that never change. The hash being in the *cheap* request is what makes the expensive one
// skippable, and this is the half of that arrangement that lives in the browser.
//
// **Everything answers with an absence rather than an exception**, exactly as `db.ts` does and for
// the reason its header gives with more force here: a player whose definition could not be cached
// has a slower second visit, and a player whose machine will not start because a cache write failed
// has lost the product.

import { openDocuments, type DocumentStore } from './db'

/** The key prefix, so the cache can find its own entries among the history and the outbox. */
export const MACHINE_PREFIX = 'machine/'

/**
 * The prefix for a tile's attract art (M8-79).
 *
 * **A separate prefix rather than the same one**, because an art payload and a definition are
 * different documents about the same version and they share a hash. Filed together, whichever
 * arrived last would answer both questions — `/arcade` would get a definition it cannot draw a
 * thumbnail from, or worse, `/play` would get four fields where it expected a machine and refuse to
 * start. The hash says *which version*; the prefix says *which question*.
 */
export const ART_PREFIX = 'art/'

/**
 * The prefix for a tile's finished picture (M8-85).
 *
 * A third question about the same version, filed apart for the reason the second one is: the hash
 * says *which machine*, the prefix says *which question*. This one's answer is a rendered bitmap
 * rather than a document about how to render one — different bytes, a different size class, and a
 * different lifetime, since a picture goes stale when the **renderer** changes and the art it was
 * drawn from does not.
 */
export const PICTURE_PREFIX = 'tile/'

/**
 * How many pieces of art to keep.
 *
 * Larger than `MAX_CACHED` and for a reason that is arithmetic rather than judgement: art is a
 * sixth of a definition — 6.5 and 8.7 KB against 38.0 and 54.2 for the two shipped machines — and a
 * roster is a page somebody scrolls past in one visit, so the number of tiles seen in a session is
 * far larger than the number of machines opened. Sixty-four is still a fraction of what sixteen
 * definitions cost.
 */
export const MAX_CACHED_ART = 64

/**
 * How many definitions to keep.
 *
 * **Judgement, not measurement.** A definition is tens of kilobytes and IndexedDB's origin quota is
 * far larger than sixteen of them, so this is not sized against a limit — it is sized against what
 * a person plausibly returns to. Sixteen is a roster nobody has yet; the number will be worth
 * revisiting when there is one to measure, and until then it says here that it was chosen rather
 * than derived.
 */
export const MAX_CACHED = 16

/** What a cached entry holds. The hash is in the key; the id and version are for eviction order. */
interface CacheEntry {
  readonly definition: unknown
  readonly machineId: string
  readonly semver: string
  /** When it was last read, so eviction can drop the least recently useful rather than the oldest. */
  readonly usedAt: number
}

const isEntry = (value: unknown): value is CacheEntry =>
  typeof value === 'object' &&
  value !== null &&
  'definition' in value &&
  typeof (value as { machineId?: unknown }).machineId === 'string'

export interface MachineCache {
  /** The definition stored under this hash, or nothing. */
  get: (definitionHash: string) => Promise<unknown | undefined>
  /** Store, and evict down to `MAX_CACHED`. Answers whether the write landed. */
  put: (definitionHash: string, definition: unknown, machineId: string, semver: string) => Promise<boolean>
  /** Every hash held, for a test and for the settings page's "clear downloads". */
  hashes: () => Promise<readonly string[]>
  /** Forget everything. */
  clear: () => Promise<void>
}

/**
 * A cache over the document store, or one that holds nothing.
 *
 * The no-op is not a degraded mode to warn about. IndexedDB is missing in some private modes,
 * blocked behind "block all cookies", refused when the quota is full, and can be evicted between
 * two visits without telling anybody — so "there is no cache" is an ordinary state, and the caller
 * that had to handle it specially would end up writing the same branch at every site.
 */
export async function openMachineCache(
  supplied?: DocumentStore | undefined,
  now: () => number = () => Date.now(),
): Promise<MachineCache> {
  return await openCache(MACHINE_PREFIX, MAX_CACHED, supplied, now)
}

/**
 * The same cache, holding what a roster tile draws rather than what a game runs (M8-79).
 *
 * Same shape, same eviction, same absence-rather-than-exception contract — and a different prefix,
 * so the two never answer each other's question. See `ART_PREFIX`.
 */
/**
 * The same cache again, holding finished pictures rather than the art they were drawn from.
 *
 * **Capped far lower**, and that is arithmetic rather than judgement: an art payload is about
 * 13 KB of JSON and a rendered tile is about 13 KB of WebP, but the picture is the one a browser
 * can afford to lose — losing it costs a re-render, where losing the art costs a request. Sixteen
 * machines is a roster page and a bit, which is what somebody actually scrolls in a visit.
 */
export async function openTilePictures(
  supplied?: DocumentStore | undefined,
  now: () => number = () => Date.now(),
): Promise<MachineCache> {
  return await openCache(PICTURE_PREFIX, MAX_CACHED_PICTURES, supplied, now)
}

/** How many finished tile pictures are kept. See `openTilePictures`. */
export const MAX_CACHED_PICTURES = 16

export async function openAttractCache(
  supplied?: DocumentStore | undefined,
  now: () => number = () => Date.now(),
): Promise<MachineCache> {
  return await openCache(ART_PREFIX, MAX_CACHED_ART, supplied, now)
}

async function openCache(
  prefix: string,
  cap: number,
  supplied: DocumentStore | undefined,
  now: () => number,
): Promise<MachineCache> {
  // Opened here rather than defaulted in the parameter list: a parameter initialiser cannot await,
  // and a test supplies its own store precisely so that no database is opened at all.
  const store = supplied ?? (await openDocuments())
  if (store === undefined) {
    return {
      get: async () => undefined,
      put: async () => false,
      hashes: async () => [],
      clear: async () => {},
    }
  }

  const keyFor = (hash: string): string => `${prefix}${hash}`

  const listing = async (): Promise<{ key: string; entry: CacheEntry }[]> => {
    const keys = await store.keys()
    const found: { key: string; entry: CacheEntry }[] = []
    for (const key of keys.filter((k) => k.startsWith(prefix))) {
      const entry = await store.get<CacheEntry>(key)
      if (isEntry(entry)) found.push({ key, entry })
    }
    return found
  }

  return {
    get: async (definitionHash) => {
      const entry = await store.get<CacheEntry>(keyFor(definitionHash))
      if (!isEntry(entry)) return undefined
      // Touched on read, so eviction drops what nobody returns to rather than what was stored
      // first. A write that fails costs nothing here: the value is already in hand.
      void store.put(keyFor(definitionHash), { ...entry, usedAt: now() })
      return entry.definition
    },

    put: async (definitionHash, definition, machineId, semver) => {
      const wrote = await store.put(keyFor(definitionHash), {
        definition,
        machineId,
        semver,
        usedAt: now(),
      } satisfies CacheEntry)
      if (!wrote) return false
      const held = await listing()
      if (held.length > cap) {
        // Oldest use first, and only the overflow. Sorted rather than "drop anything": evicting
        // the entry a player is about to open is the one eviction that is visible to them.
        const byAge = held.sort((a, b) => a.entry.usedAt - b.entry.usedAt)
        for (const stale of byAge.slice(0, held.length - cap)) await store.remove(stale.key)
      }
      return true
    },

    hashes: async () => (await listing()).map(({ key }) => key.slice(prefix.length)),

    clear: async () => {
      for (const { key } of await listing()) await store.remove(key)
    },
  }
}
